import {
  Children,
  cloneElement,
  isValidElement,
  MouseEvent,
  useEffect,
  useRef,
  useState,
} from "react";
import css from "@styled-system/css";
import styled from "styled-components";
import { SpaceProps, space, variant } from "styled-system";

import Box from "../Box/Box";
import { highlightVariants } from "./variants";
import {
  StepProps,
  SubTaskVariant,
  StepVariant,
} from "./verticalTimeline.types";
import StepHeading from "./StepHeading";

export const Highlight = styled.span<{
  variant?: SubTaskVariant;
}>`
  display: inline-block;
  width: 4px;
  height: 100%;
  border-radius: 50px;
  ${css({ backgroundColor: "grey-blue" })}
  ${variant({ variants: highlightVariants })}
`;

const StyledDetails = styled.details<{ maxHeight: number; spacing: number }>`
  ${(props) => `outline: none;
  padding-right: 12px;
  &.disabled {
    pointer-events: none;
    user-select: none;
    summary {
      opacity: 0.37;
    }
  }
  summary {
    outline: none;
  }
  summary::marker {
    content: "";
  }
  summary::-webkit-details-marker {
    display: none;
  }

  transition: max-height 0.5s ease-in-out;
  overflow: hidden;
  max-height: 73px;

  &[open] {
    max-height: ${props.maxHeight + props.spacing}px;
  }
 `}
`;

const INITIAL_SPACING = 42;
const TASK_SPACING = 16;

const StepContainer = styled.div<{ variant: StepVariant } & SpaceProps>`
  ${(props) =>
    css({
      ...(props.variant === "disabled" && { opacity: 0.37 }),
    })}
  ${space}
`;

const Step = ({
  variant,
  children,
  isLast,
  accordion = false,
  customIcon,
  stepBorderColor,
  stepBackgroundColor,
  title,
  additionalInfo,
  onChange,
}: StepProps) => {
  const numberOfSubTasks = Children.count(children);

  const [maxHeight, setMaxHeight] = useState<number>(0);
  const [shouldRecalculateHeight, setShouldRecalculateHeight] =
    useState<boolean>(false);
  const [tasksSizes, setTasksSizes] = useState<number[]>(
    new Array(numberOfSubTasks)
  );
  const [stepExpanded, setStepExpanded] = useState<boolean>(
    variant === "active" || !accordion
  );
  const [numberOfLines, setNumberOfLines] = useState(1);
  const headlineRef = useRef<HTMLHeadingElement>(null);

  const isDisabled = ["disabled", "disabled secondary"].includes(variant);

  useEffect(() => {
    const headlineNode = headlineRef.current;

    if (headlineNode) {
      const lines = headlineNode.scrollHeight / 19;

      setNumberOfLines(lines);
    }
  }, []);

  const childrenWithAddedProps = Children.map(children, (child, index) => {
    if (child !== null && isValidElement(child)) {
      return cloneElement(child, {
        isStepActive: variant === "active",
        setTasksSizes: setTasksSizes,
        setShouldRecalculateHeight,
        stepExpanded,
        index,
        tasksSizes,
        numberOfLines,
      });
    }
    return child;
  });

  const handleDetailsClick = (e: MouseEvent<HTMLElement>) => {
    if (isDisabled) {
      e.preventDefault();
      return;
    }

    const detailsElement = e.currentTarget.parentElement as HTMLDetailsElement;
    if (onChange) {
      onChange(detailsElement.open ? "collapse" : "expand");
    }
    setStepExpanded(!detailsElement.open);
  };
  const detailsRef = useRef(null);

  useEffect(() => {
    if (tasksSizes.length === numberOfSubTasks || shouldRecalculateHeight) {
      const initialValue = 0;
      const sum = tasksSizes.reduce(
        (previousValue, currentValue) => previousValue + currentValue,
        initialValue
      );

      setMaxHeight(sum);
    }
  }, [numberOfSubTasks, shouldRecalculateHeight, tasksSizes]);

  const actualNumberOfChildren = Children.count(childrenWithAddedProps);
  const shouldDisplayTimeline =
    !isLast &&
    (isDisabled || numberOfSubTasks === 0 || actualNumberOfChildren === 0);

  return accordion && actualNumberOfChildren > 0 ? (
    <Box display="flex" flexDirection="column" mb={2}>
      <StyledDetails
        open={variant === "active"}
        className={variant === "disabled" ? "disabled" : ""}
        maxHeight={maxHeight}
        spacing={INITIAL_SPACING + numberOfSubTasks * TASK_SPACING}
        ref={detailsRef}
      >
        <summary onClick={handleDetailsClick}>
          <StepHeading
            variant={variant}
            customIcon={customIcon}
            stepBackgroundColor={stepBackgroundColor}
            stepBorderColor={stepBorderColor}
            title={title}
            additionalInfo={additionalInfo}
            isExpanded={stepExpanded}
            shouldDisplayTimeline={!isLast && !stepExpanded}
            accordion
            numberOfLines={numberOfLines}
            ref={headlineRef}
          />
        </summary>
        <div>{childrenWithAddedProps}</div>
      </StyledDetails>
    </Box>
  ) : (
    <StepContainer variant={variant} mb={!isLast ? 2 : 0}>
      <StepHeading
        title={title}
        variant={variant}
        stepBackgroundColor={stepBackgroundColor}
        stepBorderColor={stepBorderColor}
        additionalInfo={additionalInfo}
        isExpanded={stepExpanded}
        shouldDisplayTimeline={shouldDisplayTimeline}
        accordion={false}
        customIcon={customIcon}
        numberOfLines={numberOfLines}
        ref={headlineRef}
      />
      {!isDisabled && childrenWithAddedProps}
    </StepContainer>
  );
};

export default Step;
